/**
 * Decimal strings for the recipe editor.
 *
 * §21 quantities are `Decimal(12,3)` and costs `Decimal(12,4)`. A JS number
 * cannot hold either faithfully, so nothing here ever calls `Number()` or
 * `parseFloat`: values are validated as text and moved between scales by
 * shifting the decimal point, which is exact by construction. What the user
 * types is what the API receives.
 */

/** Unsigned decimal literal — the only shape any of these helpers accepts. */
const DECIMAL = /^\d+(\.\d+)?$/;


/** True when `value` is a decimal with no more than `scale` fraction digits. */
export function isDecimalWithScale(value: string, scale: number): boolean {
  const text = value.trim();
  if (!DECIMAL.test(text)) return false;
  const fraction = text.split('.')[1] ?? '';
  return fraction.length <= scale;
}

/** True for `0`, `0.000`, `.` — anything that means nothing. */
export function isZeroDecimal(value: string): boolean {
  const text = value.trim();
  if (!DECIMAL.test(text)) return false;
  return /^0*(\.0*)?$/.test(text);
}

/**
 * Move the decimal point `places` digits (positive = right, ×10ⁿ). Pure text
 * surgery, so `0.0005` never becomes `5e-4` and `1.005` never becomes `100.49999`.
 */
export function shiftDecimalPoint(value: string, places: number): string {
  const text = value.trim();
  if (!DECIMAL.test(text)) return text;

  const [intPart, fracPart = ''] = text.split('.');
  let digits = intPart + fracPart;
  let point = intPart.length + places;

  if (point < 0) {
    digits = '0'.repeat(-point) + digits;
    point = 0;
  }
  if (point > digits.length) {
    digits += '0'.repeat(point - digits.length);
  }

  const head = digits.slice(0, point).replace(/^0+(?=\d)/, '') || '0';
  const tail = digits.slice(point).replace(/0+$/, '');
  return tail ? `${head}.${tail}` : head;
}

/** `"5"` (per cent, as typed) → `"0.05"` (the fraction the column stores). */
export function percentToFraction(value: string): string {
  return shiftDecimalPoint(value.trim() || '0', -2);
}

/** `"0.0500"` (as stored) → `"5"` (per cent, for the input). */
export function fractionToPercent(value: string): string {
  return shiftDecimalPoint(String(value ?? '0').trim() || '0', 2);
}

/** Drops the trailing zeros an API decimal carries, without touching its value. */
export function trimDecimal(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  if (!DECIMAL.test(text)) return text || '0';
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}
